import React, { useEffect, useMemo } from 'react';
import useVisitStore from '../../stores/visitStore';
import { useScope } from '../../context/PermissionContext';
import { useTheme } from '../../theme/ThemeContext';

/**
 * ═══════════════════════════════════════════════════════════════════════
 * ClinicCapacitySummary — Today's clinic visit overview
 *
 * Derives data from daily visits (GET /api/organisation/visits/daily)
 * Statuses: QUEUE, IN_PROGRESS, COMPLETED, CANCELLED
 * ═══════════════════════════════════════════════════════════════════════
 */
export default function ClinicCapacitySummary() {
  const { dailyVisits, fetchDailyVisits } = useVisitStore();
  const { isScoped, scopeLabel, memberId } = useScope();
  const { isDark } = useTheme();

  useEffect(() => {
    fetchDailyVisits();
  }, [fetchDailyVisits]);

  // Client-side scope filter — defense-in-depth so scoped roles
  // (DOCTOR) never see inflated totals from other doctors' visits,
  // even during the brief window before the backend-scoped fetch returns.
  const scopedVisits = useMemo(() => {
    if (!isScoped || !memberId) return dailyVisits || [];
    return (dailyVisits || []).filter(
      (v) => v.doctor_member_id === memberId || v.visit_doctor_id === memberId
    );
  }, [dailyVisits, isScoped, memberId]);

  const summary = useMemo(() => {
    return (scopedVisits).reduce(
      (acc, v) => {
        acc.total++;
        if (v.status === 'QUEUE') acc.queue++;
        else if (v.status === 'IN_PROGRESS') acc.in_progress++;
        else if (v.status === 'COMPLETED') acc.completed++;
        else if (v.status === 'CANCELLED') acc.cancelled++;
        else if (v.status === 'SKIPPED') acc.skipped++;
        return acc;
      },
      { total: 0, queue: 0, in_progress: 0, completed: 0, cancelled: 0, skipped: 0 }
    );
  }, [scopedVisits]);

  const { total, queue, in_progress, completed } = summary;

  return (
    <div
      style={{
        borderRadius: '1rem',
        overflow: 'hidden',
        border: `1px solid ${isDark ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.06)'}`,
        background: isDark ? 'rgba(255,255,255,0.03)' : 'rgba(255,255,255,0.7)',
      }}
    >
      {/* Busy indicator */}
      {queue > 10 && (
        <div
          style={{
            padding: '0.625rem 1rem',
            display: 'flex',
            alignItems: 'center',
            gap: '0.5rem',
            background: isDark ? 'rgba(245, 158, 11, 0.1)' : '#fffbeb',
            borderBottom: `1px solid ${isDark ? 'rgba(245, 158, 11, 0.2)' : '#fef3c7'}`,
          }}
        >
          <span>⚠️</span>
          <p style={{ margin: 0, fontSize: '0.8125rem', fontWeight: 500, color: isDark ? '#fbbf24' : '#92400e' }}>
            High Queue Volume — {queue} patients waiting
          </p>
        </div>
      )}

      <div style={{ padding: '1rem 1.25rem' }}>
        {/* Title Row */}
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '0.75rem' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
            <span>🏥</span>
            <h3 style={{ margin: 0, fontSize: '0.875rem', fontWeight: 600, color: isDark ? '#f5f5f7' : '#111827' }}>
              {isScoped ? `${scopeLabel} Summary` : "Today's Summary"}
            </h3>
          </div>
          <span
            style={{
              fontSize: '1.125rem',
              fontWeight: 700,
              background: 'linear-gradient(135deg, #0d9488, #14b8a6)',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
            }}
          >
            {total}
          </span>
        </div>

        {/* Progress Bar */}
        <div
          style={{
            width: '100%',
            height: '0.5rem',
            background: isDark ? 'rgba(255,255,255,0.08)' : '#f3f4f6',
            borderRadius: '9999px',
            overflow: 'hidden',
            marginBottom: '1rem',
          }}
        >
          <div style={{ height: '100%', display: 'flex' }}>
            <div style={{ width: `${(completed / (total || 1)) * 100}%`, background: '#4ade80', transition: 'width 0.5s' }} />
            <div style={{ width: `${(in_progress / (total || 1)) * 100}%`, background: '#2dd4bf', transition: 'width 0.5s' }} />
            <div style={{ width: `${(queue / (total || 1)) * 100}%`, background: '#3b82f6', transition: 'width 0.5s' }} />
          </div>
        </div>

        {/* Stats Grid */}
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '0.5rem' }}>
          <StatBox label="Total" value={total} isDark={isDark} accentColor="#0d9488" />
          <StatBox label="Done" value={completed} isDark={isDark} accentColor="#4ade80" />
          <StatBox label="Queue" value={queue} isDark={isDark} accentColor="#3b82f6" />
          <StatBox label="Active" value={in_progress} isDark={isDark} accentColor="#2dd4bf" />
        </div>
      </div>
    </div>
  );
}

function StatBox({ label, value, isDark, accentColor }) {
  return (
    <div
      style={{
        textAlign: 'center',
        padding: '0.625rem 0.5rem',
        borderRadius: '0.75rem',
        border: `1px solid ${isDark ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.06)'}`,
        background: isDark ? 'rgba(255,255,255,0.03)' : '#fff',
      }}
    >
      <p style={{ margin: 0, fontSize: '1.125rem', fontWeight: 700, color: accentColor }}>{value}</p>
      <p style={{ margin: '0.125rem 0 0', fontSize: '0.625rem', color: isDark ? '#9ca3af' : '#6b7280', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
        {label}
      </p>
    </div>
  );
}
